import { isDefined } from "@clipboard-health/util-ts";
import { useGetClipboardScoreProfile } from "@src/appV2/ClipboardScore/api/useGetClipboardScoreProfile";
import { ON_TIME_BONUS_GRACE_PERIOD_MINUTES } from "@src/appV2/ClipboardScore/constants";
import { getClockInTimingInfo } from "@src/appV2/redesign/utils/clockInTiming";
import { type ShiftStateData } from "@src/appV2/Shifts/Shift/ShiftState/types";
import { addMinutes, isAfter, parseISO, subMinutes } from "date-fns";
import { useMemo } from "react";

import type { ArrivalContext, ClockInContext } from "../types";

interface UseClockInContextParams {
  shiftStart: string;
  shiftState: ShiftStateData;
  currentDate: Date;
  arrivalState: ArrivalContext["state"];
  workerId: string;
  agentReq: string;
}
const BONUS_PRE_SHIFT_WARNING_OFFSET_MINUTES = 10; // Show warning 10 min before shift start

/**
 * Single-responsibility hook for clock in related logic
 * Only calculates when user has arrived at the facility
 */
export function useClockInContext({
  shiftStart,
  shiftState,
  currentDate,
  arrivalState,
  workerId,
  agentReq,
}: UseClockInContextParams): ClockInContext {
  const { data: clipboardScoreProfile } = useGetClipboardScoreProfile(workerId, {}, agentReq);

  return useMemo(() => {
    // Only calculate clock in info when user has arrived
    if (arrivalState !== "arrived") {
      return {
        canClockInNow: false,
        earliestClockInTime: undefined,
        isInPreArrivalWindow: false,
        isInBonusWindow: false,
        isInGracePeriod: false,
        isAlreadyLate: false,
      };
    }

    const { earliestClockInTime, canClockInNow } = getClockInTimingInfo({
      shiftStart,
      shiftState,
      currentDate,
    });

    const shiftStartTime = parseISO(shiftStart);

    // Get timing configuration from clipboard score profile
    const latenessConfig = clipboardScoreProfile?.policy?.latenessConfig ?? [];
    const firstPenaltyThreshold = Number(latenessConfig[0]?.minutesLateRange?.startFrom ?? 15);

    // Four distinct time windows:
    // 1. Pre-arrival warning: 10 min before shift → shift start (encourage on-time arrival)
    // 2. Bonus window: shift start → +5 min (still eligible for +5 bonus points)
    // 3. Grace period: +5 min → penalty threshold (missed bonus, avoid penalty)
    // 4. Penalty zone: past penalty threshold (damage control)

    const bonusWarningTime = subMinutes(shiftStartTime, BONUS_PRE_SHIFT_WARNING_OFFSET_MINUTES);
    const bonusEndTime = addMinutes(shiftStartTime, ON_TIME_BONUS_GRACE_PERIOD_MINUTES);
    const firstPenaltyTime = addMinutes(shiftStartTime, firstPenaltyThreshold);

    const isInPreArrivalWindow =
      isAfter(currentDate, bonusWarningTime) && !isAfter(currentDate, shiftStartTime);
    const isInBonusWindow =
      isAfter(currentDate, shiftStartTime) && !isAfter(currentDate, bonusEndTime);
    const isInGracePeriod =
      isAfter(currentDate, bonusEndTime) && !isAfter(currentDate, firstPenaltyTime);
    const isAlreadyLate = isAfter(currentDate, firstPenaltyTime);

    return {
      canClockInNow,
      earliestClockInTime:
        !canClockInNow && isDefined(earliestClockInTime) ? earliestClockInTime : undefined,
      isInPreArrivalWindow,
      isInBonusWindow,
      isInGracePeriod,
      isAlreadyLate,
    };
  }, [
    arrivalState,
    shiftStart,
    shiftState,
    currentDate,
    clipboardScoreProfile?.policy?.latenessConfig,
  ]);
}
